import React, { useState } from "react";
import { 
  FiClock, 
  FiUser, 
  FiCheckCircle, 
  FiAlertCircle, 
  FiFileText,
  FiFilter
} from "react-icons/fi";
import type { RcaAuditEvent } from "../../../types/rca.types.js";

export type RcaAuditHistoryProps = {
  events?: RcaAuditEvent[] | undefined;
};

const INITIAL_EVENTS: RcaAuditEvent[] = [
  { id: "ev-1", dateTime: "25-Aug-2026 10:30 AM", action: "RCA Created", performedBy: "John Smith", details: "RCA created with method 5-Why Analysis", category: "ALL" },
  { id: "ev-2", dateTime: "25-Aug-2026 10:45 AM", action: "Problem Defined", performedBy: "John Smith", details: "Problem statement and description saved", category: "ALL" },
  { id: "ev-3", dateTime: "25-Aug-2026 11:00 AM", action: "Evidence Uploaded", performedBy: "John Smith", details: "3 files uploaded to Evidence Center", category: "ALL" },
  { id: "ev-4", dateTime: "25-Aug-2026 11:30 AM", action: "Why Chain Started", performedBy: "John Smith", details: "Why levels 1 through 5 answers entered", category: "ALL" },
  { id: "ev-5", dateTime: "25-Aug-2026 02:30 PM", action: "Root Cause Identified", performedBy: "John Smith", details: "Step #5 marked as candidate Root Cause", category: "STATUS" },
  { id: "ev-6", dateTime: "25-Aug-2026 03:15 PM", action: "RCA Submitted", performedBy: "John Smith", details: "Submitted for Level 1 technical review", category: "SUBMISSIONS" },
  { id: "ev-7", dateTime: "25-Aug-2026 04:00 PM", action: "Review Started", performedBy: "Priya", details: "Technical review checklist assessment", category: "SUBMISSIONS" },
  { id: "ev-8", dateTime: "25-Aug-2026 04:30 PM", action: "Rework Requested", performedBy: "Priya", details: "More evidence requested for root cause verification", category: "SUBMISSIONS" },
];

export function RcaAuditHistory({
  events = INITIAL_EVENTS,
}: RcaAuditHistoryProps) {
  const [activeTab, setActiveTab] = useState<"ALL" | "STATUS" | "SUBMISSIONS">("ALL");

  const filtered = events.filter(e => {
    if (activeTab === "ALL") return true;
    return e.category === activeTab;
  });

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="text-xs font-semibold text-blue-600 uppercase tracking-wider">CMTS &gt; Audit Trail & Compliance</div>
          <h2 className="text-lg font-bold text-slate-900 mt-0.5">9. History & Audit Trail</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Immutable, chronological system log tracking all mutations, state transitions, and reviewer decisions.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex items-center gap-2">
        {(["ALL", "STATUS", "SUBMISSIONS"] as const).map(tabKey => (
          <button
            key={tabKey}
            type="button"
            onClick={() => setActiveTab(tabKey)}
            className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              activeTab === tabKey
                ? "bg-blue-600 text-white shadow-sm"
                : "text-slate-600 hover:bg-slate-50"
            }`}
          >
            {tabKey === "ALL" ? "All Events" : tabKey === "STATUS" ? "Status Changes" : "Submissions & Reviews"}
          </button>
        ))}
      </div>

      {/* Timeline Card */}
      <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
        <div className="relative pl-6 border-l-2 border-slate-200 space-y-6">
          {filtered.map(event => (
            <div key={event.id} className="relative space-y-1">
              <span className="absolute -left-[31px] top-1 w-3.5 h-3.5 rounded-full bg-blue-600 border-2 border-white"></span>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="font-bold text-xs text-slate-900 flex items-center gap-2">
                  <span>{event.action}</span>
                  <span className="text-slate-400 font-normal">by</span>
                  <span className="font-semibold text-blue-600">{event.performedBy}</span>
                </div>
                <div className="text-[11px] text-slate-400 font-medium">
                  {event.dateTime}
                </div>
              </div>
              <p className="text-xs text-slate-600 leading-relaxed font-medium">{event.details}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
